import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag, ArrowRight, Truck, BadgeIndianRupee, Sparkles, Lock } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { launchShiprocketCheckout } from "@/lib/shiprocket";
import { toast } from "sonner";

const CartPage = () => {
  const {
    cart,
    updateQty,
    removeFromCart,
    cartTotal,
    cartMrpTotal,
    clearCart,
  } = useStore();
  const navigate = useNavigate();
  const savings = cartMrpTotal - cartTotal;
  const shipping = cartTotal >= 499 || cartTotal === 0 ? 0 : 49;
  const total = cartTotal + shipping;

  const handleCheckout = async () => {
    try {
      toast.loading("Opening secure checkout…", { id: "sr-checkout" });
      await launchShiprocketCheckout({ cart });
      toast.dismiss("sr-checkout");
    } catch (e) {
      toast.dismiss("sr-checkout");
      toast.error(e.message || "Checkout couldn't open. Please try again.");
    }
  };

  if (cart.length === 0) {
    return (
      <section className="py-20 lg:py-32" data-testid="empty-cart">
        <div className="max-w-[600px] mx-auto px-5 text-center">
          <ShoppingBag size={48} className="text-gold mx-auto mb-6" strokeWidth={1} />
          <h1 className="font-serif text-plum leading-[1] mb-3" style={{ fontSize: "clamp(36px, 5vw, 56px)" }}>
            Your bag is <span className="italic text-blush">empty</span>
          </h1>
          <p className="text-plum/65 mb-8">Time to fill it with pieces that feel like you.</p>
          <Link to="/shop" className="btn-primary" data-testid="empty-cart-shop-cta">
            Browse the collection <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="cart-page">
      <section className="pt-12 lg:pt-20 pb-8" style={{ background: "#FBEAF0" }}>
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="gold-divider" />
            <span className="overline">Your Selection</span>
            <span className="gold-divider" />
          </div>
          <h1 className="font-serif text-plum leading-[1] tracking-tight" style={{ fontSize: "clamp(38px, 5vw, 64px)" }}>
            The <span className="italic text-blush">Bag</span>
          </h1>
        </div>
      </section>

      <section className="py-10 lg:py-16">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Items */}
          <div className="lg:col-span-8">
            <div className="bg-pearl/50 rounded-[24px] p-4 lg:p-6 divide-y divide-sectionbreak">
              {cart.map((item) => (
                <div key={item.id} className="flex items-start gap-4 py-5 first:pt-2" data-testid={`cart-item-${item.id}`}>
                  <Link to={`/product/${item.id}`} className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-cream">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link to={`/product/${item.id}`} className="font-serif text-plum text-xl hover:italic">{item.name}</Link>
                        <p className="mt-1 text-[12px] text-plum/55 tracking-wider uppercase">In stock · Ships in 2 days</p>
                      </div>
                      <button
                        onClick={() => { removeFromCart(item.id); toast.success("Removed from bag"); }}
                        className="text-plum/50 hover:text-blush p-1"
                        aria-label="Remove"
                        data-testid={`remove-${item.id}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center border border-gold/40 rounded-full">
                        <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-9 h-9 flex items-center justify-center text-plum hover:text-blush" data-testid={`qty-minus-${item.id}`}>
                          <Minus size={12} />
                        </button>
                        <span className="w-7 text-center text-sm font-medium" data-testid={`qty-${item.id}`}>{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.qty + 1)} className="w-9 h-9 flex items-center justify-center text-plum hover:text-blush" data-testid={`qty-plus-${item.id}`}>
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-plum font-medium">₹{item.price * item.qty}</span>
                        {item.mrp && item.mrp > item.price && (
                          <span className="text-plum/40 text-sm line-through">₹{item.mrp * item.qty}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-5 px-2">
              <Link to="/shop" className="text-sm tracking-[0.14em] uppercase text-plum/70 hover:text-blush">← Continue shopping</Link>
              <button onClick={() => { clearCart(); toast.success("Bag cleared"); }} className="text-sm tracking-[0.14em] uppercase text-plum/70 hover:text-blush" data-testid="clear-cart">
                Clear bag
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-4">
            <div className="sticky top-32 bg-cream border border-sectionbreak rounded-[24px] p-6 lg:p-7">
              <h3 className="font-serif text-plum text-2xl mb-5">Order Summary</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-plum/75">
                  <span>Subtotal</span>
                  <span>₹{cartMrpTotal}</span>
                </div>
                {savings > 0 && (
                  <div className="flex justify-between text-blush">
                    <span>You save</span>
                    <span>−₹{savings}</span>
                  </div>
                )}
                <div className="flex justify-between text-plum/75">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? "Free" : `₹${shipping}`}</span>
                </div>
                {shipping > 0 && (
                  <p className="text-[11px] text-gold tracking-wide bg-sectionbreak rounded-lg px-3 py-2">
                    Add ₹{499 - cartTotal} more for free shipping
                  </p>
                )}
              </div>
              <div className="flex justify-between items-baseline mt-5 pt-5 border-t border-sectionbreak">
                <span className="font-serif text-xl text-plum">Total</span>
                <span className="font-serif text-2xl text-plum" data-testid="cart-total">₹{total}</span>
              </div>
              <button
                onClick={handleCheckout}
                className="btn-primary w-full mt-6"
                data-testid="checkout-button"
              >
                <Lock size={13} /> Checkout — 1 click
                <ArrowRight size={14} />
              </button>
              <p className="text-[11px] text-plum/55 mt-3 text-center tracking-wide">
                ✦ Secure Shiprocket checkout · UPI · Cards · COD · Phone-OTP
              </p>
              <div className="mt-6 space-y-2.5 text-[12px] text-plum/65">
                <p className="flex items-center gap-2"><Truck size={13} className="text-gold" /> Free delivery on orders above ₹499</p>
                <p className="flex items-center gap-2"><BadgeIndianRupee size={13} className="text-gold" /> COD available Pan India</p>
                <p className="flex items-center gap-2"><Sparkles size={13} className="text-gold" /> Tarnish-free, hypoallergenic</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CartPage;
